'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Text, Tooltip } from '@terra/ui-design-system';
import type { TerraIcon } from '@terra/ui-icons';
import type { MouseEventHandler } from 'react';
import { getSidebarNavItemStyleProps } from './sidebarNavItemStyles';
import { handleButtonKeyDown } from './handleButtonKeyDown';
import { IndicatorDot } from './IndicatorDot';

type SidebarNavItemProps = {
  icon: TerraIcon;
  label: string;
  isActive?: boolean;
  isSubItem?: boolean;
  hasNew?: boolean;
  showIndicatorDot?: boolean;
  collapsed?: boolean;
  disabled?: boolean;
  tourId?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  onContextMenu?: MouseEventHandler<HTMLElement>;
};

export function SidebarNavItem({
  icon: Icon,
  label,
  isActive,
  isSubItem,
  hasNew,
  showIndicatorDot,
  collapsed,
  disabled,
  tourId,
  onClick,
  onContextMenu,
}: SidebarNavItemProps) {
  const styleProps = getSidebarNavItemStyleProps({
    isActive: Boolean(isActive),
    disabled,
  });

  if (collapsed) {
    return (
      <Tooltip title={label} positioning={{ placement: 'right' }} openDelay={0}>
        <Box
          display="flex"
          justifyContent="center"
          w="full"
          data-tour-id={tourId}
        >
          <HStack
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={label}
            w="8"
            h="8"
            justifyContent="center"
            alignItems="center"
            borderRadius="md"
            position="relative"
            cursor={disabled ? 'not-allowed' : 'pointer'}
            opacity={disabled ? 0.4 : undefined}
            onClick={disabled ? undefined : onClick}
            onKeyDown={disabled ? undefined : handleButtonKeyDown}
            onContextMenu={disabled ? undefined : onContextMenu}
            {...styleProps}
          >
            <Box position="relative" display="flex" alignItems="center">
              <Icon size={14} strokeWidth={1.75} />
              {(showIndicatorDot || hasNew) && (
                <IndicatorDot
                  color={showIndicatorDot ? 'orange.9' : 'terra.9'}
                />
              )}
            </Box>
          </HStack>
        </Box>
      </Tooltip>
    );
  }

  return (
    <HStack
      role="button"
      data-tour-id={tourId}
      tabIndex={disabled ? -1 : 0}
      w="full"
      h="8"
      gap="2"
      alignItems="center"
      px="2"
      pl={isSubItem ? '7' : '2'}
      borderRadius="md"
      textAlign="left"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.4 : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : handleButtonKeyDown}
      onContextMenu={disabled ? undefined : onContextMenu}
      {...styleProps}
    >
      <Box
        flexShrink={0}
        position="relative"
        display="flex"
        alignItems="center"
      >
        <Icon size={14} strokeWidth={1.75} />
        {showIndicatorDot && <IndicatorDot color="orange.9" />}
      </Box>
      <Text
        flex="1"
        minW="0"
        truncate
        textStyle={isActive ? 'body.strong' : 'body'}
        color="inherit"
      >
        {label}
      </Text>
      {hasNew && <IndicatorDot position="inline" />}
    </HStack>
  );
}
